import { useQuery, useQueries, queryOptions } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useRequiredParams } from "../../utils/url";
import { ApiResponse } from "../../api/api-response.model";
import { QueryOptions } from "../../utils/consts";
import { assetsApiRequest } from "../../api/api-request";
import {
  DataType,
  dataTypeUnits,
  EnergyResolution,
  getReportData,
} from "./point-energy.utils";
import { Level } from "../../level/level.model";

export type EnergyReportParams = {
  companyId: number;
  deviceId?: number;
  pointIds?: number[];
  dataType?: DataType;
  buildingId?: number | null;
  from?: string;
  to?: string;
  resolution: EnergyResolution;
};

export type PointEnergyReport = ReturnType<typeof getReportData>[0];

export type PointEnergyReportDTO = {
  name: string;
  deviceId: string;
  description?: string;
  values: number[];
};

const energyReportQueries = {
  energyReport: (params: EnergyReportParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["energy-report", params],
      queryFn: ({ signal }) => fetchEnergyReport(params, signal),
      ...options,
    }),
  buildings: (params: { companyId: number }, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["tree", "buildings", params],
      queryFn: () => fetchBuildings(params),
      ...options,
    }),
};

// Queries

export function useEnergyReportQuery(
  params: Omit<EnergyReportParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery({
    ...energyReportQueries.energyReport(
      {
        ...params,
        companyId,
      },
      options,
    ),
  });
}

export function useFetchBuildings(options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });

  return useQuery(energyReportQueries.buildings({ companyId }, options));
}

export function useEnergyReportQueries(
  paramsList: Omit<EnergyReportParams, "companyId">[],
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });

  return useQueries({
    queries: paramsList.map((params) =>
      energyReportQueries.energyReport(
        {
          ...params,
          companyId,
        },
        options,
      ),
    ),
  });
}

// Fetchers

async function fetchEnergyReport(
  { companyId, ...params }: EnergyReportParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponse<PointEnergyReportDTO[]>>(
      `analytics/companies/${companyId}/energy-report`,
      {
        params: {
          ...params,
          tz: DateTime.local().zoneName,
          ...(params.dataType ? { unit: dataTypeUnits[params.dataType] } : {}),
        },
        signal,
      },
    )
    .then((resp) => resp.data.data);
}

async function fetchBuildings({ companyId }: { companyId: number }) {
  return assetsApiRequest
    .get<ApiResponse<Level[]>>(`analytics/companies/${companyId}/buildings`)
    .then((resp) => resp.data.data.map((l) => new Level(l)));
}
